import React, { useCallback, useEffect, useRef, useState } from 'react';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CircularProgress from '@mui/material/CircularProgress';
import { GetSetupDemoStatusAPI, SendSetupDemoAPI } from '../../../api/API';
import { formatPhone } from '../../../utils/phoneUtils';
import DemoConfirmedDialog from '../components/DemoConfirmedDialog';
import DemoTimeline from '../components/DemoTimeline';
import StepFooter from '../components/StepFooter';
import WhatsAppPhonePreview from '../components/WhatsAppPhonePreview';
import shell from '../SetupWizard.module.css';
import styles from './steps.module.css';
import own from './DemoStep.module.css';

const POLL_MS = 4000;
const POLL_LIMIT = 75; // ~5 minutos no ritmo rápido; depois segue mais espaçado
const SLOW_POLL_MS = 15000;

const ANSWERED_STATUSES = ['confirmed', 'confirmed_by_patient', 'canceled_by_patient'];

// Envio automático uma vez só por número na aba: StrictMode, voltar e avançar
// ou recarregar a página nunca mandam de novo sem clique
const AUTO_SENT_KEY = 'gabriela.setupDemo.autoSent';
// O consentimento do passo anterior não é gravado no servidor: fica junto da marca
// do envio automático para o texto do passo continuar o mesmo depois de recarregar
const CONSENT_KEY = 'gabriela.setupDemo.consent';
const autoSentFallback = new Set();
let consentFallback = false;

function rememberedConsent() {
  try {
    return window.sessionStorage.getItem(CONSENT_KEY) === '1';
  } catch {
    return consentFallback;
  }
}

function alreadyAutoSent(digits) {
  try {
    return (window.sessionStorage.getItem(AUTO_SENT_KEY) || '').split(',').includes(digits);
  } catch {
    return autoSentFallback.has(digits);
  }
}

function markAutoSent(digits) {
  autoSentFallback.add(digits);
  consentFallback = true;
  try {
    window.sessionStorage.setItem(CONSENT_KEY, '1');
    const list = (window.sessionStorage.getItem(AUTO_SENT_KEY) || '').split(',').filter(Boolean);
    window.sessionStorage.setItem(AUTO_SENT_KEY, [...list, digits].join(','));
  } catch {
    // sem sessionStorage vale o Set da memória
  }
}

/**
 * Passo final: "Veja a Gabriela trabalhando". A pessoa é a paciente do agendamento
 * de teste e recebe no WhatsApp a confirmação com botões (SetupDemo.php).
 * Com a caixa do passo anterior marcada (data.identity.demo_consent), o envio
 * sai sozinho ao entrar no passo, uma vez; sem ela, depende de "Enviar teste".
 * O celular desenhado mostra a mesma mensagem (SetupDemo::preview).
 */
function DemoStep({ data, wizard }) {
  const state = data.demo.state;
  const consent = Boolean(data.identity?.demo_consent ?? rememberedConsent());
  const [phone, setPhone] = useState(formatPhone((consent && data.identity?.whatsapp) || data.demo.phone || ''));
  const [sending, setSending] = useState(false);
  const [autoSending, setAutoSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [sentTo, setSentTo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [polls, setPolls] = useState(0);
  const [celebrate, setCelebrate] = useState(false);
  const sawWaiting = useRef(false);
  const { setDemoState } = wizard;

  const answered = state && ANSWERED_STATUSES.includes(state.status);
  // Esperando a resposta enquanto a mensagem não falhou: a consulta nunca para,
  // só fica mais espaçada (quem demora a abrir o WhatsApp ainda vê a tela mudar)
  const waiting = Boolean(state && !answered && state.message_status !== 'failed');

  useEffect(() => {
    if (!waiting) return undefined;
    const timer = setTimeout(() => {
      GetSetupDemoStatusAPI((response) => {
        if (response?.success && response.state) setDemoState(response.state);
        setPolls((count) => count + 1);
      });
    }, polls < POLL_LIMIT ? POLL_MS : SLOW_POLL_MS);
    return () => clearTimeout(timer);
  }, [waiting, polls, setDemoState]);

  // Voltou para a aba (depois de tocar no botão no celular ou no computador): consulta na hora
  useEffect(() => {
    if (!waiting) return undefined;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      GetSetupDemoStatusAPI((response) => {
        if (response?.success && response.state) setDemoState(response.state);
      });
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [waiting, setDemoState]);

  // Texto e botões do celular desenhado (o envio devolve o que de fato saiu e tem prioridade)
  useEffect(() => {
    GetSetupDemoStatusAPI((response) => {
      if (response?.success && response.preview) setPreview((current) => current || response.preview);
    }, true);
  }, []);

  const send = useCallback((target) => {
    const digits = target.replace(/\D/g, '');
    setSending(true);
    setSendError(null);
    SendSetupDemoAPI({ phone: digits }, (response) => {
      setSending(false);
      setAutoSending(false);
      if (!response?.success) {
        setSendError(response?.error || 'Não foi possível enviar agora. Tente de novo.');
        return;
      }
      setSentTo(formatPhone(digits));
      if (response.preview) setPreview(response.preview);
      setPolls(0);
      setDemoState(response.state);
    });
  }, [setDemoState]);

  // Consentimento dado no passo anterior e nada enviado ainda: envia sozinho, uma vez
  useEffect(() => {
    const digits = phone.replace(/\D/g, '');
    if (!consent || state || digits.length < 10 || alreadyAutoSent(digits)) return;
    markAutoSent(digits);
    setAutoSending(true);
    send(phone);
    // Só ao entrar no passo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validPhone = phone.replace(/\D/g, '').length >= 10;
  // Enquanto o teste não saiu, "Enviar teste" é a ação principal e concluir vira link discreto
  const notSent = !state || state.message_status === 'failed';
  const showForm = notSent && !autoSending;
  const delivered = state && state.message_status !== 'failed';
  const typing = sending || state?.message_status === 'sending';
  const failed = Boolean(sendError) || state?.message_status === 'failed';
  let answer = null;
  if (state && ['confirmed', 'confirmed_by_patient'].includes(state.status)) answer = 'confirmed';
  else if (state?.status === 'canceled_by_patient') answer = 'canceled';

  // A comemoração só abre quando a confirmação chega com a tela esperando
  // (quem volta ao passo já confirmado não vê o popup de novo)
  useEffect(() => {
    if (waiting) sawWaiting.current = true;
    if (answer === 'confirmed' && sawWaiting.current) {
      sawWaiting.current = false;
      setCelebrate(true);
    }
  }, [waiting, answer]);

  return (
    <>
      <h2 className={shell.stepTitle}>Veja a Gabriela trabalhando</h2>
      <p className={shell.stepHint}>
        {/* "Mandamos" só quando o envio saiu de fato; enviando, em falha ou sem saber
            (depois de recarregar), o texto fica neutro */}
        {consent && delivered && 'Marcamos um horário de teste com você e mandamos para o seu WhatsApp a mensagem de confirmação que fica pronta antes de cada atendimento. Com o seu número conectado, ela sai sozinha, com botões, como você acabou de receber; sem ele, você envia pelo WhatsApp Web com um clique e a pessoa responde pelo link.'}
        {consent && !delivered && 'Este é o teste no seu WhatsApp: a mensagem de confirmação que fica pronta antes de cada atendimento. Com o seu número conectado, ela sai sozinha, com botões; sem ele, você envia pelo WhatsApp Web com um clique e a pessoa responde pelo link.'}
        {!consent && 'Vamos marcar um atendimento de teste com você no lugar de quem você atende. Em instantes chega no seu WhatsApp a mensagem de confirmação que fica pronta antes de cada atendimento. Com o seu número conectado, ela sai sozinha, com botões; sem ele, você envia pelo WhatsApp Web com um clique e a pessoa responde pelo link.'}
      </p>

      <div className={own.container}>
        {/* Sem prévia (WhatsApp indisponível) o andamento ocupa a largura toda */}
        <div className={preview ? own.layout : undefined}>
          <div className={own.main}>
            {autoSending && !state && (
              <p className={own.notice}>
                <CircularProgress size={16} thickness={5} />
                <span>Enviando para <strong>{phone}</strong>...</span>
              </p>
            )}
            {sentTo && delivered && (
              <p className={`${own.notice} ${own.noticeDone}`}>
                <WhatsAppIcon fontSize="small" />
                <span>Enviamos agora para <strong>{sentTo}</strong>. Confira no seu celular.</span>
              </p>
            )}

            {state && <DemoTimeline state={state} waiting={waiting} sender={preview?.sender} />}

            {showForm && (
              <div className={styles.demoIntro}>
                <span className={styles.demoIcon}><WhatsAppIcon /></span>
                <label className={`${shell.field} ${styles.demoField}`}>
                  <span className={shell.fieldLabel}>Seu WhatsApp</span>
                  <input
                    className={shell.input}
                    value={phone}
                    inputMode="tel"
                    maxLength={16}
                    placeholder="(11) 99999-9999"
                    onChange={(event) => setPhone(event.target.value)}
                    onBlur={() => setPhone(formatPhone(phone))}
                  />
                  <span className={shell.fieldHint}>
                    Use o seu número: aqui você faz o papel do paciente.
                  </span>
                </label>
                <button
                  type="button"
                  className={`${shell.primaryButton} ${styles.demoSend}`}
                  onClick={() => send(phone)}
                  disabled={!validPhone || sending}
                >
                  <WhatsAppIcon fontSize="small" />
                  {sending ? 'Enviando...' : state || sendError ? 'Enviar de novo' : 'Enviar teste'}
                </button>
                {/* A falha fica junto do "Enviar de novo" (fora da faixa fixa, que cobriria o botão no celular) */}
                {failed ? (
                  <p className={styles.demoFailed} role="alert">
                    <strong>Não conseguimos mandar agora.</strong>
                    {sendError && ` ${sendError}`}
                  </p>
                ) : (
                  <p className={styles.demoPreview}>
                    Em segundos chega no seu WhatsApp a confirmação de um horário de teste, com botões para
                    você responder.
                  </p>
                )}
              </div>
            )}
          </div>

          {preview && (
            <aside className={own.phone}>
              <WhatsAppPhonePreview preview={preview} typing={typing} answer={answer} pointConfirm={waiting && delivered && !typing} />
              <p className={own.caption}>
                {!state && 'Prévia do que chega no seu celular'}
                {state && (waiting && delivered ? 'No seu celular, toque no botão em destaque' : 'A conversa no seu celular')}
              </p>
            </aside>
          )}
        </div>
      </div>

      <DemoConfirmedDialog
        open={celebrate}
        date={state?.date}
        onClose={() => setCelebrate(false)}
        onPrimary={() => {
          setCelebrate(false);
          wizard.finish();
        }}
        primaryLabel="Concluir configuração"
        saving={wizard.saving}
      />

      <StepFooter
        error={wizard.error?.message}
        onBack={wizard.back}
        onPrimary={wizard.finish}
        primaryLabel={notSent ? 'Pular o teste e concluir' : 'Concluir configuração'}
        primaryAsLink={notSent}
        saving={wizard.saving}
      />
    </>
  );
}

export default DemoStep;
